import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Lock } from "lucide-react";
import { cloakListPortfolios, formatApiError, toastApiError } from "@/lib/api";

const fmtUsd = (n) =>
  Number(n).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

export default function CloakUserPage() {
  const { userId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setData(await cloakListPortfolios(userId));
      } catch (e) {
        toastApiError(e);
        nav("/admin", { replace: true });
      }
    })();
  }, [userId, nav]);

  if (!data) return <div className="text-[var(--aurion-muted)]">Loading…</div>;
  const { cloaked_user: u, portfolios } = data;

  return (
    <div data-testid="cloak-view">
      <Button
        data-testid="back-to-admin"
        onClick={() => nav("/admin")}
        variant="ghost"
        className="mb-4 pl-0 text-xs uppercase tracking-widest text-[var(--aurion-muted)] hover:bg-transparent hover:text-[var(--aurion-text)]"
      >
        <ArrowLeft className="mr-1 h-3.5 w-3.5" />
        Back to admin
      </Button>

      <div className="mb-6 flex items-baseline gap-3">
        <h1 className="text-xs uppercase tracking-widest text-[var(--aurion-muted)]">
          Cloaked user
        </h1>
        <span className="font-mono text-[var(--aurion-text)]" data-testid="cloaked-user-email">
          {u.email}
        </span>
        <span className="text-xs text-[var(--aurion-muted)]">({u.name})</span>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {portfolios.length === 0 ? (
          <Card
            data-testid="cloak-empty"
            className="col-span-full border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-10 text-center text-sm text-[var(--aurion-muted)]"
          >
            {u.email} has no portfolios yet.
          </Card>
        ) : (
          portfolios.map((p) => (
            <Card
              key={p.id}
              data-testid={`cloak-portfolio-${p.id}`}
              className="border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-5"
            >
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                  Portfolio
                </div>
                <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-red-300">
                  <Lock className="h-3 w-3" />
                  read-only
                </span>
              </div>
              <div className="mt-1 text-lg text-[var(--aurion-text)]">{p.name}</div>
              <div className="mt-4 font-mono text-2xl" style={{ color: "var(--aurion-gold)" }}>
                {fmtUsd(p.cash)}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                starting · {fmtUsd(p.starting_cash)}
              </div>
              <div className="mt-3 text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                created {new Date(p.created_at).toLocaleDateString()}
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
